/**
 * Login Page
 * Welcome screen with Google Sign-In
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { useAuth } from '@/contexts/AuthContext';

// Detect in-app browsers (LinkedIn, WhatsApp, Instagram, Facebook, etc.)
// Google blocks OAuth in these WebViews with error 403: disallowed_useragent.
// Capacitor's WebView has `wv)` in its UA too, so we short-circuit when native.
const isInAppBrowser = (): boolean => {
  if (Capacitor.isNativePlatform()) return false;
  const ua = navigator.userAgent;
  return /FBAN|FBAV|Instagram|WhatsApp|LinkedInApp|Line\/|MicroMessenger|Snapchat|Twitter|TikTok|musical_ly/i.test(ua) ||
    (/Android/i.test(ua) && /wv\)/i.test(ua)) ||
    (/iPhone|iPad/i.test(ua) && !/Safari\//i.test(ua) && /AppleWebKit/i.test(ua));
};

const Login = () => {
  const navigate = useNavigate();
  const { user, loading, signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [copied, setCopied] = useState(false);
  const inAppBrowser = isInAppBrowser();

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      setErrorMsg('');
      await signInWithGoogle();
    } catch (err: any) {
      console.error('Sign-in error:', err);
      setErrorMsg(err?.code ? `Error: ${err.code}` : (err?.message || 'Sign-in failed. Please try again.'));
      setIsLoading(false);
    }
  };

  const copyLink = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Fallback for WebViews that block the clipboard API
      const el = document.createElement('textarea');
      el.value = url;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isBusy = isLoading || loading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF5F5] via-[#FFF0F0] to-[#FFE8E8] flex flex-col">
      {/* Top Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-8 pb-4">
        <div className="w-44 h-44 mb-8 flex items-center justify-center">
          <img
            src="/numaa_logo_nobg.png"
            alt="numaa"
            className="w-full h-full object-contain"
            style={{ imageRendering: 'auto' }}
          />
        </div>

        <h1 className="text-2xl font-bold text-gray-800 text-center mb-3">
          Every Moment Matters
        </h1>

        <p className="text-sm text-gray-600 text-center max-w-[320px] leading-relaxed">
          Nurture your little one's journey with love. Track every feed, cherish
          each milestone, and watch your baby bloom. 💛
        </p>
      </div>

      {/* Bottom Section - Sign In Card */}
      <div className="px-6 pb-8">
        <div className="bg-white rounded-3xl p-6 shadow-xl max-w-md mx-auto">
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-1">Welcome, New Parent</h2>
            <p className="text-sm text-gray-500">Your parenting journey starts here</p>
          </div>

          {inAppBrowser ? (
            <div className="text-center">
              <p className="text-2xl mb-2">🌐</p>
              <p className="text-sm font-semibold text-gray-800 mb-1">Open in your browser</p>
              <p className="text-xs text-gray-500 mb-5">to sign in with Google</p>

              <button
                onClick={copyLink}
                className="w-full py-3.5 px-4 bg-orange-500 text-white rounded-xl active:scale-[0.98] transition-all"
              >
                <span className="block text-sm font-semibold">
                  {copied ? 'Copied!' : 'Copy link'}
                </span>
                {!copied && (
                  <span className="block text-xs text-orange-100 mt-0.5">(paste link in browser)</span>
                )}
              </button>
            </div>
          ) : (
            <button
              onClick={handleSignIn}
              disabled={isBusy}
              className="w-full py-3.5 px-4 bg-white border border-gray-300 rounded-xl font-medium text-gray-700 shadow-sm hover:shadow-md hover:bg-gray-50 active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isBusy ? (
                <>
                  <div className="w-5 h-5 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M19.6 10.227c0-.709-.064-1.39-.182-2.045H10v3.868h5.382a4.6 4.6 0 01-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" fill="#4285F4" />
                    <path d="M10 20c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H1.064v2.59A9.996 9.996 0 0010 20z" fill="#34A853" />
                    <path d="M4.405 11.9c-.2-.6-.314-1.24-.314-1.9 0-.66.114-1.3.314-1.9V5.51H1.064A9.996 9.996 0 000 10c0 1.614.386 3.14 1.064 4.49l3.34-2.59z" fill="#FBBC05" />
                    <path d="M10 3.977c1.468 0 2.786.505 3.823 1.496l2.868-2.868C14.959.99 12.695 0 10 0 6.09 0 2.71 2.24 1.064 5.51l3.34 2.59C5.19 5.736 7.395 3.977 10 3.977z" fill="#EA4335" />
                  </svg>
                  <span>Continue with Google</span>
                </>
              )}
            </button>
          )}

          {errorMsg && (
            <p className="mt-2 text-xs text-center text-red-500 font-medium">{errorMsg}</p>
          )}

          <p className="mt-4 text-xs text-center text-gray-400 leading-relaxed">
            By continuing, you agree to our{' '}
            <span className="text-gray-500">Terms of Service</span> and{' '}
            <span className="text-gray-500">Privacy Policy</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
